import React, { useId, useMemo } from 'react'
import { Panel } from 'primereact/panel'
import { Tag } from 'primereact/tag'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'

import {
    ChargePoint,
    useChargePointDisableOcppMutation,
    useChargePointEnableOcppMutation,
    useChargePointUpdateChargerInformationMutation,
    useExternalServerGetAllQuery,
    useExternalServerGetByChargePointIdQuery,
    usePartnerGetExternalServersQuery,
} from '../../redux/api/web-api'
import { mapChargerToChargerInformation } from '../../utils/objectMappers'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'
import { FieldInfoButton } from '../../uikit/field-info-button'
import { ChargePointExternalServerWidget } from '../../features/chargepoint-external-server-widget'
import { isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import { UserState } from '../../redux/features/user/userSlice'
import Text from '../../../ui/Text'

export type OcppProxyPanelProps = {
    chargePoint: ChargePoint
}

export function OcppProxyPanel({ chargePoint }: OcppProxyPanelProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const partnerHelpId = useId()
    const partnerOcppIntegrationSupportEmail = text('lblPartnerOcppIntegrationSupportEmail')
    const partnerId = useSelector((state: { user: UserState }) => state.user.partnerId)
    const assignedExternalServerId = chargePoint.additionalInfo?.externalServerId
    const { data: supportExternalServers } = useExternalServerGetAllQuery(undefined, {
        refetchOnFocus: true,
        skip: isPartnerDomain,
    })
    const { data: partnerExternalServers } = usePartnerGetExternalServersQuery(
        { partnerId: partnerId ?? 0 },
        { skip: !isPartnerDomain || !partnerId, refetchOnFocus: true }
    )
    const { currentData: assignedExternalServer } = useExternalServerGetByChargePointIdQuery(
        { chargePointId: chargePoint.id },
        { skip: !isPartnerDomain || !assignedExternalServerId, refetchOnFocus: true }
    )
    const externalServersOptions = useMemo(() => {
        if (!isPartnerDomain) return supportExternalServers

        const availableExternalServers = partnerExternalServers ?? []
        if (
            !assignedExternalServer ||
            assignedExternalServer.id !== assignedExternalServerId ||
            availableExternalServers.some((server) => server.id === assignedExternalServer.id)
        ) {
            return availableExternalServers
        }

        return [...availableExternalServers, assignedExternalServer]
    }, [assignedExternalServer, assignedExternalServerId, partnerExternalServers, supportExternalServers])
    const partnerHasNoProxyOptions =
        isPartnerDomain && partnerExternalServers?.length === 0 && !assignedExternalServerId

    const [chargePointEnableOcpp, { isLoading: enableLoading }] = useChargePointEnableOcppMutation()
    const [chargePointDisableOcpp, { isLoading: disableLoading }] = useChargePointDisableOcppMutation()
    const [updateChargePoint] = useChargePointUpdateChargerInformationMutation()

    const toggleOcppAvailability = async (forceUpdate: boolean = false) => {
        try {
            if (chargePoint.ocppEnabled) {
                await chargePointDisableOcpp({
                    chargePointId: chargePoint.id,
                    ...(forceUpdate && { forceUpdate }),
                }).unwrap()
                toast.notifySuccess(text('lblRelayDisabled'))
            } else {
                await chargePointEnableOcpp({
                    chargePointId: chargePoint.id,
                    ...(forceUpdate && { forceUpdate }),
                }).unwrap()
                toast.notifySuccess(text('lblRelayEnabled'))
            }
        } catch (error) {
            toast.notifyError(text('lblProxyDisableEnableError'))
        }
    }

    const updateExternalServer = async (externalServerId?: number | null) => {
        await updateChargePoint({
            chargerInformation: {
                ...mapChargerToChargerInformation(chargePoint),
                externalServerId,
            },
            chargePointId: chargePoint.id,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgChargerUpdated'))
            })
            .catch(() => {
                toast.notifyError(text('msgChargePointNotUpdated'))
            })
    }

    const disabledToggleCpms =
        chargePoint.isCharging || (!chargePoint.additionalInfo?.externalServerId && !chargePoint.ocppEnabled)
    const disabledToggleReason = chargePoint.isCharging
        ? text('msgOcppProxyToggleDisabledWhileCharging')
        : !chargePoint.additionalInfo?.externalServerId && !chargePoint.ocppEnabled
          ? text('msgOcppProxyToggleRequiresSelection')
          : undefined

    return (
        <Panel
            data-testid="relay-heading"
            header={
                <>
                    {text('lblRelayWidget')}
                    <Tag
                        className="ml-2"
                        severity={chargePoint.ocppEnabled ? 'success' : null}
                        value={chargePoint.ocppEnabled ? text('lblEnabledStatus') : text('lblDisabledStatus')}
                    />
                </>
            }
            className="flex-1 min-w-0"
            icons={
                <div className="flex align-items-center gap-3">
                    {isSupportDomain && (
                        <Link
                            className="p-button p-component p-button-outlined p-button-sm no-underline"
                            to="/externalservers"
                            data-testid="external-server-redirect"
                        >
                            <span className="p-button-label">{text('lblGoToProxy')}</span>
                        </Link>
                    )}

                    <FieldInfoButton
                        ariaLabel={text('lblMoreInformation')}
                        text={text('lblProxyRelayInfoText')}
                        testId="ocpp-proxy-info-button"
                    />
                </div>
            }
        >
            {isPartnerDomain && (
                <p
                    id={partnerHelpId}
                    className="mt-0 mb-3 text-600 text-sm"
                    data-testid="partner-integration-picker-help"
                >
                    <Text
                        code="msgPartnerOcppIntegrationPickerHelp"
                        values={{
                            supportEmail: partnerOcppIntegrationSupportEmail,
                            link: {
                                href: `mailto:${partnerOcppIntegrationSupportEmail}`,
                            },
                        }}
                    />
                </p>
            )}
            <ChargePointExternalServerWidget
                externalServersOptions={externalServersOptions}
                enableLoading={enableLoading}
                disableLoading={disableLoading}
                disabledToggleCpms={disabledToggleCpms}
                disabledToggleReason={disabledToggleReason}
                chargePoint={chargePoint}
                disableCpms={!!chargePoint.ocppEnabled || partnerHasNoProxyOptions}
                disabledPickerReason={
                    partnerHasNoProxyOptions ? text('msgPartnerOcppIntegrationPickerNoOptions') : undefined
                }
                pickerDescribedBy={isPartnerDomain ? partnerHelpId : undefined}
                updateExternalServer={updateExternalServer}
                toggleOcppAvailability={toggleOcppAvailability}
            />
        </Panel>
    )
}
